import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, rupiah } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { Seo } from "@/components/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Button } from "@/components/ui/button";
import { shareDriverToWA } from "@/lib/shareSchedule";
import { MapPin, ArrowRight, MapPinned, Star, Share2, Bus } from "lucide-react";

export default function AllRoutes() {
  const [cities, setCities] = useState([]);
  const [popular, setPopular] = useState([]);
  const [drivers, setDrivers] = useState([]);

  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data)).catch(() => {});
    api.get("/routes/popular").then((r) => setPopular(r.data)).catch(() => {});
    api.get("/drivers/list").then((r) => setDrivers(r.data)).catch(() => {});
  }, []);

  const byProv = cities.reduce((acc, c) => {
    const p = c.province || "Lainnya";
    (acc[p] = acc[p] || []).push(c);
    return acc;
  }, {});
  const provinces = Object.keys(byProv).sort();

  return (
    <div className="min-h-screen bg-background" data-testid="all-routes-page">
      <Seo
        path="/rute"
        title="Semua Rute & Kota Travel Door to Door & Charter Drop Off | Travele"
        description="Direktori lengkap kota keberangkatan & tujuan travel door to door dan charter drop off antarkota di Indonesia. Temukan jadwal, harga, dan armada di Digital Terminal Travele."
      />
      <Navbar />
      <Breadcrumbs items={[{ name: "Beranda", path: "/" }, { name: "Semua Rute & Kota" }]} />

      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <p className="font-mono-terminal text-xs uppercase tracking-widest text-primary">Direktori Rute</p>
        <h1 className="mt-2 font-display text-3xl font-black uppercase tracking-tighter sm:text-4xl lg:text-5xl">Semua Rute &amp; Kota Layanan</h1>
        <p className="mt-3 max-w-3xl leading-relaxed text-muted-foreground">
          Jelajahi seluruh kota keberangkatan dan tujuan Travel Door to Door serta Charter Drop Off di Digital Terminal Travele.
          Pilih kota untuk melihat rute, jadwal, harga tiket, dan armada yang tersedia. Semua pemesanan dibantu Admin lewat WhatsApp resmi.
        </p>

        {popular.length > 0 && (
          <>
            <h2 className="mb-4 mt-10 font-display text-2xl font-bold tracking-tight">Rute Populer</h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {popular.map((r) => (
                <Link key={r.slug} to={`/${r.slug}`} className="group border border-border bg-card p-4 transition-colors hover:border-primary" data-testid={`all-popular-${r.slug}`}>
                  <div className="flex items-center justify-between font-mono-terminal text-[10px] uppercase text-muted-foreground">
                    <span>{r.service_type === "charter" ? "Charter" : "Door to Door"}</span>
                    <ArrowRight className="h-4 w-4 text-primary opacity-0 transition-opacity group-hover:opacity-100" />
                  </div>
                  <div className="mt-1 font-display font-bold">{r.origin} → {r.destination}</div>
                  <div className="text-xs text-muted-foreground">Mulai {rupiah(r.min_price)}</div>
                </Link>
              ))}
            </div>
          </>
        )}

        <h2 className="mb-2 mt-12 font-display text-2xl font-bold tracking-tight">Kota Layanan per Provinsi</h2>
        <p className="mb-4 max-w-3xl text-sm text-muted-foreground">
          Klik kota untuk melihat semua rute travel door to door dan charter drop off dari/menuju kota tersebut.
        </p>
        {provinces.length === 0 && <p className="text-muted-foreground">Memuat daftar kota...</p>}
        {provinces.map((prov) => (
          <div key={prov} className="mt-6" data-testid={`prov-${prov.replace(/\s+/g, "-").toLowerCase()}`}>
            <h3 className="mb-3 flex items-center gap-2 font-display text-lg font-bold"><MapPinned className="h-4 w-4 text-primary" /> {prov}</h3>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {byProv[prov].map((c) => (
                <div key={c.slug} className="border border-border bg-card p-4" data-testid={`all-city-${c.slug}`}>
                  <div className="flex items-center gap-1 font-semibold"><MapPin className="h-3.5 w-3.5 text-primary" /> {c.name}</div>
                  <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-sm">
                    <Link to={`/travel-dari-${c.slug}`} className="text-primary hover:underline">Travel dari {c.name}</Link>
                    <Link to={`/travel-ke-${c.slug}`} className="text-primary hover:underline">Travel ke {c.name}</Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {drivers.length > 0 && (
          <>
            <h2 className="mb-2 mt-12 font-display text-2xl font-bold tracking-tight">Driver / PO Travel Terverifikasi</h2>
            <p className="mb-4 max-w-3xl text-sm text-muted-foreground">
              Jelajahi profil lengkap driver &amp; PO travel yang terdaftar di Travele — armada, rating, ulasan, dan jadwal keberangkatan aktif. Bagikan profil favorit Anda ke teman via WhatsApp.
            </p>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {drivers.map((d) => (
                <div key={d.slug} className="flex flex-col border border-border bg-card p-4" data-testid={`all-driver-${d.slug}`}>
                  <Link to={`/driver/${d.slug}`} className="group">
                    <div className="flex items-center gap-2 font-display font-bold group-hover:text-primary">
                      <Bus className="h-4 w-4 text-primary" /> {d.business_name}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                      {d.city && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {d.city}</span>}
                      <span className="flex items-center gap-1"><Star className="h-3 w-3 fill-primary text-primary" /> {d.avg_rating || "Baru"} ({d.rating_count || 0})</span>
                      <span>{d.total_trips || 0} perjalanan</span>
                    </div>
                  </Link>
                  <div className="mt-3 flex gap-2">
                    <Link to={`/driver/${d.slug}`} className="flex-1">
                      <Button variant="secondary" size="sm" className="w-full" data-testid={`view-driver-${d.slug}`}>Lihat Profil</Button>
                    </Link>
                    <Button
                      size="sm"
                      onClick={() => shareDriverToWA(d)}
                      className="gap-1.5 bg-[#25D366] font-bold text-black hover:bg-[#25D366]/90"
                      data-testid={`share-driver-${d.slug}`}
                    >
                      <Share2 className="h-3.5 w-3.5" /> Bagikan
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
